import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogoImage } from '@/components/ui/LogoImage';
import { Colors } from '@/constants/Colors';
import { useTheme } from '@/hooks/useTheme';
import type { Match } from '@/types';

interface MatchCardProps {
  match: Match;
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (date.toDateString() === today.toDateString()) return "Aujourd'hui";
  if (date.toDateString() === tomorrow.toDateString()) return 'Demain';
  return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function MatchCard({ match }: MatchCardProps) {
  const navigate = useNavigate();
  const colors = useTheme();

  const heureTrim = (match.heure ?? '').trim();
  const time = heureTrim ? heureTrim.slice(0, 5) : '--:--';
  const hasScore = match.home_score !== null && match.away_score !== null;
  const competitionLabel = (match.competition ?? '').trim();

  const goToMatch = () => navigate(`/match/${match.idmatch_football}`);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={goToMatch}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          goToMatch();
        }
      }}
      style={{ ...styles.container, backgroundColor: colors.surface, borderColor: colors.border }}
    >
      <div style={styles.matchHeader}>
        <div style={styles.teamColumn}>
          <div style={{ ...styles.logoContainer, backgroundColor: colors.background }}>
            <LogoImage uri={match.home_logo} size={42} />
          </div>
          <div style={{ ...styles.teamName, color: colors.text }}>{match.homeTeam}</div>
        </div>

        <div style={styles.centerColumn}>
          {hasScore ? (
            <div style={{ ...styles.score, color: colors.text }}>
              {match.home_score} - {match.away_score}
            </div>
          ) : (
            <div style={{ ...styles.score, color: colors.text }}>{time}</div>
          )}
          <div style={{ fontSize: 10, marginTop: 4, color: colors.textSecondary, textAlign: 'center' }}>
            {formatDate(match.date)}
          </div>
          {competitionLabel ? (
            <div
              style={{
                marginTop: 4,
                fontSize: 10,
                fontWeight: 700,
                color: Colors.primary,
                textAlign: 'center',
                lineHeight: 1.2,
                width: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-word',
              }}
            >
              {competitionLabel}
            </div>
          ) : null}
        </div>

        <div style={styles.teamColumn}>
          <div style={{ ...styles.logoContainer, backgroundColor: colors.background }}>
            <LogoImage uri={match.away_logo} size={42} />
          </div>
          <div style={{ ...styles.teamName, color: colors.text }}>{match.awayTeam}</div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 16,
    padding: 14,
    border: '1px solid',
    marginBottom: 12,
    width: '100%',
    maxWidth: '100%',
    cursor: 'pointer',
    textAlign: 'left',
    boxSizing: 'border-box',
    WebkitAppearance: 'none',
    appearance: 'none',
    font: 'inherit',
    color: 'inherit',
    WebkitTapHighlightColor: 'transparent',
    transition: 'transform 0.15s',
  },
  matchHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 0,
    width: '100%',
    minWidth: 0,
  },
  teamColumn: {
    flex: '1 1 0%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
  },
  centerColumn: {
    flex: '0 0 92px',
    width: 92,
    minWidth: 92,
    maxWidth: 92,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexShrink: 0,
    boxSizing: 'border-box',
    paddingLeft: 2,
    paddingRight: 2,
  },
  logoContainer: {
    width: 58,
    height: 58,
    minWidth: 58,
    minHeight: 58,
    borderRadius: 29,
    padding: 7,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  teamName: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.25,
    textAlign: 'center',
    textTransform: 'uppercase',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
    hyphens: 'auto',
    minWidth: 0,
    width: '100%',
    maxWidth: '100%',
  },
  score: {
    fontSize: 24,
    fontWeight: 900,
    fontStyle: 'italic',
    lineHeight: 1,
    letterSpacing: -1,
  },
};
